"use client";

import { getGlobalSearchSuggestions } from "@/app/actions/AiCourseFinderActions";
import { useAuth } from "@/hook/useAuth";
import { useDebounce } from "@/hook/useDebounce";
import { setAiGlobalSearch } from "@/store/feature/ai-course-finder/aiCourseFinderSlice";
import { useAppDispatch } from "@/store/hooks";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { useEffect, useRef, useState } from "react";
import { PinkSearch } from "../common/Icons";
import { Search } from "lucide-react";
interface CourseSuggestion {
  course_id: string | number;
  college_name: string;
  college_slug: string;
  course_slug: string;
  course_details: {
    course_name: string;
    course_specialisation: string;
  };
}
const HeroInputSearch = ({
  className,
  isHeroSection,
  onSearch,
  placeholder = "Search by college, course, or subject....",
}: {
  className?: string;
  isHeroSection?: boolean;
  onSearch?: boolean;
  placeholder?: string;
}) => {
  const router = useRouter();
  const dispatch = useAppDispatch();
  const { isAnonymous } = useAuth();
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [suggestedList, setSuggestedList] = useState<CourseSuggestion[]>([]);
  const [inputValue, setInputValue] = useState("");
  const searchRef = useRef<HTMLDivElement>(null);
  const debouncedQuery = useDebounce(inputValue, 500);

  const limit = isAnonymous ? 6 : 100;

  useEffect(() => {
    if (!debouncedQuery) return;

    const fetchCourseSuggestions = async (query: string) => {
      const trimmedQuery = query.trimStart();

      if (trimmedQuery === "") return;

      try {
        const payload = {
          query,
          limit,
        };

        const response = await getGlobalSearchSuggestions(payload);
        const data = response;

        if (!data.message) {
          setSuggestedList(data.perfectMatch);
        } else {
          setSuggestedList(data.suggestion);
        }
      } catch (error) {
        console.error("Error fetching suggestions:", error);
      }
    };

    fetchCourseSuggestions(inputValue);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedQuery]);

  useEffect(() => {
    setSuggestedList([]);
  }, [dispatch]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        searchRef.current &&
        !searchRef.current.contains(event.target as Node)
      ) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const HighlightText = ({ text }: { text: string }) => {
    if (!inputValue) return <>{text}</>;
    const regex = new RegExp(`(${inputValue})`, "gi");
    const parts = text.split(regex);
    return (
      <>
        {parts.map((part, index) =>
          part.toLowerCase() === inputValue.toLowerCase() ? (
            <span key={index} className="bg-yellow-200">
              {part}
            </span>
          ) : (
            part
          )
        )}
      </>
    );
  };
  const onAiSearchHandler = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (inputValue.trim() === "") return;
    dispatch(setAiGlobalSearch(inputValue));
    router.push("/ai-course-finder");
    document.body.classList.remove("!overflow-hidden");
  };

  useEffect(() => {
    onAiSearchHandler(
      new Event("submit") as unknown as React.FormEvent<HTMLFormElement>,
    );
  }, [onSearch]);

  return (
    <>
      <div
        ref={searchRef}
        className={`max-w-[450px] 2xl:max-w-[500px] w-full relative  rounded-3xl z-[41] ${className} ${!isHeroSection && "input_gradient_border"}`}
      >
        <form onSubmit={(e) => onAiSearchHandler(e)} className="relative z-90 flex items-center">
          <Search className="size-5 absolute left-4 text-[#8E9B9B] z-30 pointer-events-none" />
          <input
            onChange={(e) => {
              setInputValue(e.target.value);
              setSuggestedList([]);
            }}
            onFocus={() => setShowSuggestions(true)}
            value={inputValue}
            className="w-full placeholder:text-[#B7B7B7] placeholder:text-sm sm:placeholder:text-base text-sm sm:text-base focus:outline-hidden rounded-3xl py-3 3xl:py-[15px] my-[5px] mx-[5px] pl-11 pr-9 3xl:pr-[50px] text-black font-semibold placeholder:font-semibold relative z-20"
            type="text"
            placeholder={placeholder}
          />
          {!isHeroSection && (
            <button
              type="submit"
              className="size-5 cursor-pointer 3xl:size-[28px] absolute top-1/2 -translate-y-1/2 right-[17px] z-30"
            >
              <PinkSearch color="#1776f0" />
            </button>
          )}
        </form>
        <div
          className={`absolute w-full bg-white top-[75px] p-3 rounded-2xl flex flex-col gap-3 max-h-[400px] shadow-2xl overflow-y-auto duration-300 ease-in-out text-black z-[50] ${showSuggestions && inputValue ? "scale-100 translate-y-0" : "scale-0 -translate-y-1/2"}`}
        >
          {suggestedList.length <= 0 && (
            <p className="text-black p-2 font-semibold">Loading.....</p>
          )}
          {suggestedList.map((item, index) => (
            <Link
              onClick={() => document.body.classList.remove("!overflow-hidden")}
              href={`/course/${item.course_id}`}
              key={index}
              className="border-b-2 p-2 rounded-2xl text-black"
            >
              <span className="font-semibold">
                <HighlightText text={item.course_details.course_name} />
              </span>{" "}
              {item.course_details.course_specialisation && (
                <span className="italic">
                  <HighlightText
                    text={item.course_details.course_specialisation}
                  />
                </span>
              )}{" "}
              <span className="font-bold">
                <HighlightText text={item.college_name} />
              </span>
            </Link>
          ))}
        </div>
      </div>
    </>
  );
};
export default HeroInputSearch;
